import { Fragment, useEffect, useMemo, useState } from "react";
import { collection, doc, orderBy, serverTimestamp, setDoc, where } from "firebase/firestore";
import { ActionIcon, Badge, Box, Button, Card, Checkbox, FileButton, Group, Image, Input, Modal, MultiSelect, Progress, SegmentedControl, Select, SimpleGrid, Stack, Table, TagsInput, Text, TextInput, ThemeIcon, Title, Tooltip } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { IconCalendar, IconHistory, IconEye, IconFileSpreadsheet, IconMail, IconMailForward, IconPhoto, IconReportAnalytics, IconRestore } from "@tabler/icons-react";
import {
    CTC_REPORT_BROKER_COMMISSION_COLUMNS, CTC_REPORT_DEFAULT_STATES, CTC_REPORT_LOGO_MAX_BYTES, CTC_REPORT_MAX_DAYS, CTC_REPORT_MAX_EMAILS, CTC_REPORT_REQUESTS, CTC_REPORT_SETTINGS_DOC, CTC_REPORT_STATES, CTC_REPORTS,
    CtcReportFilters, CtcReportKind, CtcReportRequest, CtcReportSendStatus, CtcReportSettings, ctcParseLogo, ctcReportColumnLabel, ctcReportEmailProblem, ctcReportRequestProblem,
} from "../../../../shared/ctcReports";
import { CtcPoState } from "../../../../shared/ctcLifecycle";
import { ctcHidesBrokerCommission } from "../../../../shared/ctcAccess";
import { CtcCurrency, CtcCustomer } from "../../types";
import { useFirestore } from "../../helpers/firebaseContext";
import { useCtcCallables, useCtcUser } from "./ctc_helpers";
import { useLiveCollection, useLiveDoc } from "./useLive";
import { useLivePagedQuery } from "./useLivePagedQuery";
import { CtcPager, CtcQueryError } from "./ctc_pager";
import { CtcPanel } from "./ctc_panel";

/*
 * CTC → Reports: one card per report; Generate opens a dialog with the period, filters and columns.
 * ctcRequestReport queues it: the Excel is built in the background and emailed to the requester and
 * the report list (ctc_config/reports.emails); nothing is downloaded or stored. Report history
 * (ctc_report_requests) shows every request with its sends; Re-send builds it again and emails it.
 * Back office sets the logo (ctc_config/reports.logo, base64) and the email list. Chosen columns
 * are remembered per report in this browser.
 */

const DESCRIPTIONS: Record<CtcReportKind, string> = {
    summary: "One line per order (completed by default): customer, broker, trading type, value in the transaction currency and in KWD{broker}.",
    details: "Full detail per order (completed by default): commodity, quantity, price, certificate, values in the customer's, broker's and base currency, CTC commission and users.",
};

const storageKey = (kind: CtcReportKind) => `ctcReportColumns:${kind}`;
/** The report's columns this user may choose: bank makers / checkers never get the broker commission. */
const visibleColumns = (kind: CtcReportKind, hideBroker: boolean) =>
    CTC_REPORTS[kind].columns.filter((c) => !(hideBroker && CTC_REPORT_BROKER_COMMISSION_COLUMNS.has(c.key)));
const description = (kind: CtcReportKind, hideBroker: boolean) => DESCRIPTIONS[kind].replace("{broker}", hideBroker ? "" : ", broker commission");
const allKeys = (kind: CtcReportKind) => CTC_REPORTS[kind].columns.map((c) => c.key);
const loadColumns = (kind: CtcReportKind) => {
    try {
        const saved = JSON.parse(localStorage.getItem(storageKey(kind)) ?? "null") as string[] | null;
        const known = new Set(allKeys(kind));
        const valid = saved?.filter((k) => known.has(k));
        return valid?.length ? valid : allKeys(kind);
    } catch {
        return allKeys(kind);
    }
};
/** Today in Kuwait as yyyy-mm-dd (report days are Kuwait days, whatever the browser's timezone). */
const kuwaitToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kuwait" });

type Period = "custom" | "month" | "lastMonth" | "6m" | "year";
const PERIODS: { value: Period; label: React.ReactNode }[] = [
    { value: "custom", label: <Group gap={6} wrap="nowrap"><IconCalendar size={16} />Custom</Group> },
    { value: "month", label: "This month" },
    { value: "lastMonth", label: "Last month" },
    { value: "6m", label: "Last 6 months" },
    { value: "year", label: "This year" },
];
/** Range of a quick period; "Last 6 months" = this month and the 5 full months before it. */
const periodRange = (period: Exclude<Period, "custom">, today: string): [string, string] => {
    const [y, m] = today.split("-").map(Number);
    const day = (yy: number, mm: number, dd: number) => new Date(Date.UTC(yy, mm - 1, dd)).toISOString().slice(0, 10);
    switch (period) {
        case "month": return [day(y, m, 1), today];
        case "lastMonth": return [day(y, m - 1, 1), day(y, m, 0)];
        case "6m": return [day(y, m - 5, 1), today];
        case "year": return [day(y, 1, 1), today];
    }
};

const EMPTY_FILTERS: CtcReportFilters = { states: CTC_REPORT_DEFAULT_STATES, reference: "", customerId: "", clientRef: "", beneficiaryNo: "", currencyId: "" };

function GenerateReportModal({ kind, onClose }: { kind: CtcReportKind; onClose: () => void }) {
    const { requestReport } = useCtcCallables();
    const user = useCtcUser();
    const customers = (useLiveCollection<CtcCustomer>("ctc_customers").data ?? []).filter((c) => !c.deleted);
    const currencies = (useLiveCollection<CtcCurrency>("ctc_currencies").data ?? []).filter((c) => !c.deleted);
    const [filters, setFilters] = useState<CtcReportFilters>(EMPTY_FILTERS);
    const setFilter = (patch: Partial<CtcReportFilters>) => setFilters((prev) => ({ ...prev, ...patch }));
    const today = kuwaitToday();
    const hideBroker = ctcHidesBrokerCommission(user);
    const definition = useMemo(() => ({ ...CTC_REPORTS[kind], columns: visibleColumns(kind, hideBroker) }), [kind, hideBroker]);
    const [period, setPeriod] = useState<Period>("month");
    const [range, setRange] = useState<[string | null, string | null]>(() => periodRange("month", today));
    const choosePeriod = (p: Period) => {
        setPeriod(p);
        if (p !== "custom") setRange(periodRange(p, today));
    };
    const [columns, setColumns] = useState<string[]>(() => loadColumns(kind).filter((k) => definition.columns.some((c) => c.key === k)));
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        try { localStorage.setItem(storageKey(kind), JSON.stringify(columns)); } catch { /* storage unavailable: just don't remember */ }
    }, [kind, columns]);

    // Columns grouped as in the Excel header (ungrouped ones first)
    const sections = useMemo(() => {
        const map = new Map<string, typeof definition.columns>();
        definition.columns.forEach((c) => map.set(c.group ?? "", [...(map.get(c.group ?? "") ?? []), c]));
        return [...map.entries()];
    }, [definition]);

    const problem = ctcReportRequestProblem({ report: kind, from: range[0] ?? undefined, to: range[1] ?? undefined, columns });

    const generate = async () => {
        if (problem) return;
        setBusy(true);
        try {
            const res = await requestReport({ report: kind, from: range[0]!, to: range[1]!, columns, filters: { ...filters, customerId: user.isDesk ? filters.customerId || undefined : undefined } });
            const others = res.data.recipients - 1;
            notifications.show({ color: "green", title: "Report queued", message: `It will be emailed to you${others > 0 ? ` and ${others} other${others > 1 ? "s" : ""}` : ""}. Follow it in Report history.` });
            onClose();
        } catch (e) {
            // No matching orders: nothing is saved; the dialog stays open to change the filters
            if ((e as { code?: string }).code === "functions/not-found") notifications.show({ color: "orange", title: "No orders", message: (e as Error).message });
            else notifications.show({ color: "red", title: "Report not queued", message: (e as Error).message });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal opened onClose={() => !busy && onClose()} size="64rem" title={<Text fw={600}>{definition.title}</Text>}>
            <Stack gap="lg">
                <Card withBorder padding="md" radius="md">
                    <Group justify="space-between" mb="sm">
                        <Text size="sm" fw={600}>Filters</Text>
                        <Button size="compact-xs" variant="subtle" color="gray" leftSection={<IconRestore size={14} />} onClick={() => { setFilters(EMPTY_FILTERS); choosePeriod("month"); }}>Reset</Button>
                    </Group>
                    <Input.Wrapper label="Request date" description={`Kuwait time, up to ${CTC_REPORT_MAX_DAYS} days`} required mb="md">
                        <Group gap="sm" wrap="wrap" align="center">
                            <Box style={{ maxWidth: "100%", overflowX: "auto" }}>
                                <SegmentedControl data={PERIODS} value={period} onChange={(v) => choosePeriod(v as Period)} radius="md" color="blue" />
                            </Box>
                            {/* Always shows the chosen range; picking dates here switches to Custom */}
                            <DatePickerInput type="range" placeholder="Start – end" value={range} valueFormat="DD/MM/YYYY" maxDate={today} w={240}
                                leftSection={<IconCalendar size={16} />} aria-label="Request date range"
                                onChange={(v) => { setRange(v as [string | null, string | null]); setPeriod("custom"); }} />
                        </Group>
                    </Input.Wrapper>
                    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
                        <MultiSelect label="PO status" description="Empty = all statuses" data={CTC_REPORT_STATES} value={filters.states ?? []}
                            onChange={(v) => setFilter({ states: v as CtcPoState[] })} clearable searchable />
                        <TextInput label="Reference number" description="Starts with, e.g. MBK-25-03" value={filters.reference ?? ""} onChange={(e) => setFilter({ reference: e.currentTarget.value })} maxLength={60} />
                        {user.isDesk ? (
                            <Select label="Customer" description="Empty = all customers" data={customers.map((c) => ({ value: c.id!, label: c.name_en }))}
                                value={filters.customerId || null} onChange={(v) => setFilter({ customerId: v ?? "" })} clearable searchable />
                        ) : (
                            <TextInput label="Customer" description="Your customer" value={customers.find((c) => c.id === user.customerId)?.name_en ?? ""} readOnly />
                        )}
                        <TextInput label="Client ref no." description="Contains" value={filters.clientRef ?? ""} onChange={(e) => setFilter({ clientRef: e.currentTarget.value })} maxLength={60} />
                        <TextInput label="Beneficiary number" description="Contains" value={filters.beneficiaryNo ?? ""} onChange={(e) => setFilter({ beneficiaryNo: e.currentTarget.value })} maxLength={60} />
                        <Select label="Account currency" description="Order currency; empty = all" data={currencies.map((c) => ({ value: c.id!, label: `${c.shortName} — ${c.name_en}` }))}
                            value={filters.currencyId || null} onChange={(v) => setFilter({ currencyId: v ?? "" })} clearable searchable />
                    </SimpleGrid>
                </Card>

                <div>
                    <Group justify="space-between" mb={8}>
                        <Text size="sm" fw={500}>Columns <Text span c="dimmed" size="sm">({columns.length} of {definition.columns.length})</Text></Text>
                        <Group gap={4}>
                            <Button size="compact-xs" variant="subtle" onClick={() => setColumns(definition.columns.map((c) => c.key))}>Select all</Button>
                            <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setColumns([])}>Clear</Button>
                        </Group>
                    </Group>
                    <Checkbox.Group value={columns} onChange={(v) => setColumns(definition.columns.map((c) => c.key).filter((k) => v.includes(k)))}>
                        <Stack gap="sm">
                            {sections.map(([group, cols]) => (
                                <Card key={group || "general"} withBorder padding="sm" radius="md">
                                    {group && <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={8}>{group}</Text>}
                                    <SimpleGrid cols={{ base: 1, xs: 2, sm: 3 }} spacing={8} verticalSpacing={8}>
                                        {cols.map((c) => <Checkbox key={c.key} size="sm" value={c.key} label={c.label} />)}
                                    </SimpleGrid>
                                </Card>
                            ))}
                        </Stack>
                    </Checkbox.Group>
                </div>

                <Group justify="space-between">
                    <Text size="xs" c={problem && range[0] && range[1] ? "red" : "dimmed"}>
                        {problem && range[0] && range[1] ? problem : "Built in the background and emailed to you and the report list. Amounts in other currencies use the rates saved on each order."}
                    </Text>
                    <Group>
                        <Button variant="default" onClick={onClose} disabled={busy}>Cancel</Button>
                        <Button leftSection={<IconMail size={16} />} onClick={generate} loading={busy} disabled={!!problem}>Email report</Button>
                    </Group>
                </Group>
            </Stack>
        </Modal>
    );
}

/** dd/mm/yyyy hh:mm in Kuwait time (all CTC report times are Kuwait time). */
const kuwaitTime = (ts?: { toDate?: () => Date }) => ts?.toDate?.().toLocaleString("en-GB", { timeZone: "Asia/Kuwait", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).replace(",", "");

function ReportLogoModal({ settings, onClose }: { settings?: CtcReportSettings; onClose: () => void }) {
    const db = useFirestore();
    const user = useCtcUser();
    const current = settings?.logo;
    const [logo, setLogo] = useState<string | undefined>(current);
    const [saving, setSaving] = useState(false);

    const pick = (file: File | null) => {
        if (!file) return;
        if (!["image/png", "image/jpeg"].includes(file.type)) return notifications.show({ color: "red", message: "Choose a PNG or JPG image" });
        if (file.size > CTC_REPORT_LOGO_MAX_BYTES) return notifications.show({ color: "red", message: `The logo must be at most ${CTC_REPORT_LOGO_MAX_BYTES / 1024} KB` });
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = String(reader.result);
            if (!ctcParseLogo(dataUrl)) return notifications.show({ color: "red", message: "This image can't be read. Try another PNG or JPG." });
            setLogo(dataUrl);
        };
        reader.readAsDataURL(file);
    };
    const save = async (value: string | null) => {
        setSaving(true);
        try {
            await setDoc(doc(db, CTC_REPORT_SETTINGS_DOC), { logo: value, logoUpdatedBy: user.email, logoUpdatedAt: serverTimestamp(), updated: serverTimestamp(), updatedBy: user.email }, { merge: true });
            notifications.show({ color: "green", message: value ? "Report logo saved" : "Report logo removed" });
            onClose();
        } catch (e) {
            notifications.show({ color: "red", message: (e as Error).message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal opened onClose={() => !saving && onClose()} title={<Text fw={600}>Report logo</Text>}>
            <Stack>
                <Text size="sm" c="dimmed">Printed at the top left of every Excel report. PNG or JPG, up to {CTC_REPORT_LOGO_MAX_BYTES / 1024} KB; a wide logo works best.</Text>
                <Card withBorder padding="lg" radius="md" style={{ display: "grid", placeItems: "center", minHeight: 110 }}>
                    {logo ? <Image src={logo.startsWith("data:") ? logo : `data:image/png;base64,${logo}`} alt="Report logo" mah={70} maw={260} fit="contain" /> : <Text size="sm" c="dimmed">No logo: reports are generated without one.</Text>}
                </Card>
                {settings?.logoUpdatedBy && (
                    <Text size="xs" c="dimmed">
                        Last updated {kuwaitTime(settings.logoUpdatedAt) ?? "earlier"} (Kuwait) by {settings.logoUpdatedBy}
                        {logo !== current && <Text span c="orange" size="xs"> · new image not saved yet</Text>}
                    </Text>
                )}
                <Group justify="space-between">
                    <Group gap="xs">
                        <FileButton onChange={pick} accept="image/png,image/jpeg">{(props) => <Button variant="light" leftSection={<IconPhoto size={16} />} {...props}>Choose image</Button>}</FileButton>
                        {current && <Button variant="subtle" color="red" onClick={() => save(null)} disabled={saving}>Remove</Button>}
                    </Group>
                    <Group gap="xs">
                        <Button variant="default" onClick={onClose} disabled={saving}>Cancel</Button>
                        <Button onClick={() => save(logo ?? null)} loading={saving} disabled={!logo || logo === current}>Save</Button>
                    </Group>
                </Group>
            </Stack>
        </Modal>
    );
}

function ReportEmailsModal({ settings, onClose }: { settings?: CtcReportSettings; onClose: () => void }) {
    const db = useFirestore();
    const user = useCtcUser();
    const [emails, setEmails] = useState<string[]>(settings?.emails ?? []);
    const [saving, setSaving] = useState(false);
    const problems = emails.map(ctcReportEmailProblem).filter(Boolean);
    const unchanged = JSON.stringify(emails) === JSON.stringify(settings?.emails ?? []);

    const save = async () => {
        setSaving(true);
        try {
            await setDoc(doc(db, CTC_REPORT_SETTINGS_DOC), { emails, emailsUpdatedBy: user.email, emailsUpdatedAt: serverTimestamp(), updated: serverTimestamp(), updatedBy: user.email }, { merge: true });
            notifications.show({ color: "green", message: "Report emails saved" });
            onClose();
        } catch (e) {
            notifications.show({ color: "red", message: (e as Error).message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal opened onClose={() => !saving && onClose()} size="lg" title={<Text fw={600}>Report emails</Text>}>
            <Stack>
                <Text size="sm" c="dimmed">
                    Every report is emailed to the person who generated it and to these addresses, for every customer.
                    Only {["kfh.com", "kfhcapital.com.kw", "kfhbrokerage.com"].join(", ")} addresses.
                </Text>
                <TagsInput label="Email addresses" placeholder="name@kfhcapital.com.kw, then Enter" value={emails} maxTags={CTC_REPORT_MAX_EMAILS}
                    onChange={(v) => setEmails([...new Set(v.map((e) => e.trim().toLowerCase()).filter(Boolean))])} splitChars={[",", " ", ";"]} clearable
                    error={problems[0]} />
                {settings?.emailsUpdatedBy && (
                    <Text size="xs" c="dimmed">Last updated {kuwaitTime(settings.emailsUpdatedAt) ?? "earlier"} (Kuwait) by {settings.emailsUpdatedBy}</Text>
                )}
                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={save} loading={saving} disabled={unchanged || problems.length > 0}>Save</Button>
                </Group>
            </Stack>
        </Modal>
    );
}

const STATUS: Record<CtcReportSendStatus, { label: string; color: string }> = {
    queued: { label: "Queued", color: "gray" },
    running: { label: "Preparing", color: "blue" },
    sent: { label: "Sent", color: "green" },
    empty: { label: "No orders", color: "orange" },
    failed: { label: "Failed", color: "red" },
};
const isBusy = (r: CtcReportRequest) => r.status === "queued" || r.status === "running";
const nf = (n?: number) => (n ?? 0).toLocaleString("en-US");
const fmtDay = (iso: string) => iso.split("-").reverse().join("/");

function StatusBadge({ r }: { r: CtcReportRequest }) {
    const s = STATUS[r.status] ?? STATUS.queued;
    return (
        <Stack gap={2}>
            <Badge variant="light" color={s.color}>{s.label}</Badge>
            {r.status === "running" && <Text size="xs" c="dimmed">{nf(r.progress)} orders read</Text>}
            {r.status === "failed" && r.error && <Text size="xs" c="red" lineClamp={2}>{r.error}</Text>}
        </Stack>
    );
}

function useResend() {
    const { resendReport } = useCtcCallables();
    const [busyId, setBusyId] = useState<string | null>(null);
    const resend = async (r: CtcReportRequest) => {
        setBusyId(r.id!);
        try {
            const res = await resendReport({ requestId: r.id! });
            notifications.show({ color: "green", title: "Re-send queued", message: `${r.title} will be built again and emailed to ${res.data.recipients} address${res.data.recipients > 1 ? "es" : ""}.` });
        } catch (e) {
            notifications.show({ color: "red", title: "Re-send failed", message: (e as Error).message });
        } finally {
            setBusyId(null);
        }
    };
    return { resend, busyId };
}

function ReportRequestModal({ r, onClose }: { r: CtcReportRequest; onClose: () => void }) {
    const user = useCtcUser();
    const { resend, busyId } = useResend();
    const definition = CTC_REPORTS[r.report];
    const columns = definition.columns.filter((c) => r.columns.includes(c.key));
    const line = (label: string, value: React.ReactNode) => (
        <Table.Tr><Table.Td c="dimmed" w={170} style={{ verticalAlign: "top" }}>{label}</Table.Td><Table.Td fw={500}>{value}</Table.Td></Table.Tr>
    );
    return (
        <Modal opened onClose={onClose} size="64rem" title={<Text fw={600}>{r.title}</Text>}>
            <Stack>
                <Table withRowBorders fz="sm">
                    <Table.Tbody>
                        {line("Status", <StatusBadge r={r} />)}
                        {line("Request date", `${fmtDay(r.from)} – ${fmtDay(r.to)} (Kuwait)`)}
                        {(r.filterLines ?? []).map((f) => <Fragment key={f.label}>{line(f.label, f.value)}</Fragment>)}
                        {line("Columns", columns.length === definition.columns.length ? `All ${columns.length}` : `${columns.length} of ${definition.columns.length}: ${columns.map(ctcReportColumnLabel).join(", ")}`)}
                        {line("Requested by", `${r.requestedByName} (${r.requestedBy})`)}
                        {line("Requested at", `${kuwaitTime(r.requestedAt) ?? "…"} (Kuwait)`)}
                        {line("Request ID", r.id)}
                    </Table.Tbody>
                </Table>
                <Text fw={600} size="sm">Sends ({r.sends.length})</Text>
                <Table.ScrollContainer minWidth={760}>
                    <Table striped fz="sm">
                        <Table.Thead>
                            <Table.Tr><Table.Th>#</Table.Th><Table.Th>When (Kuwait)</Table.Th><Table.Th>By</Table.Th><Table.Th>Status</Table.Th><Table.Th>Orders</Table.Th><Table.Th>Files</Table.Th><Table.Th>Sent to</Table.Th></Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {[...r.sends].reverse().map((s, i) => (
                                <Table.Tr key={s.id}>
                                    <Table.Td>{r.sends.length - i}{s.resend && <Badge ml={6} size="xs" variant="light">Re-send</Badge>}</Table.Td>
                                    <Table.Td>{kuwaitTime(s.at)}{s.finishedAt && <Text size="xs" c="dimmed">done {kuwaitTime(s.finishedAt)}</Text>}</Table.Td>
                                    <Table.Td>{s.byName}<Text size="xs" c="dimmed">{s.by}</Text></Table.Td>
                                    <Table.Td>
                                        <Badge variant="light" color={STATUS[s.status]?.color}>{STATUS[s.status]?.label ?? s.status}</Badge>
                                        {s.error && <Text size="xs" c="red">{s.error}</Text>}
                                    </Table.Td>
                                    <Table.Td>{s.rows !== undefined ? nf(s.rows) : "—"}</Table.Td>
                                    <Table.Td>{s.parts ? `${s.parts} · ${((s.bytes ?? 0) / 1048576).toFixed(1)} MB` : "—"}</Table.Td>
                                    <Table.Td><Text size="xs">{s.recipients?.join(", ") ?? "—"}</Text></Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
                <Group justify="space-between">
                    <Text size="xs" c="dimmed">Files aren't stored: Re-send builds the report again with the same filters and emails it to the current list.</Text>
                    <Group>
                        <Button variant="default" onClick={onClose}>Close</Button>
                        {!user.isViewOnly && <Button leftSection={<IconMailForward size={16} />} onClick={() => resend(r)} loading={busyId === r.id} disabled={isBusy(r)}>Re-send</Button>}
                    </Group>
                </Group>
            </Stack>
        </Modal>
    );
}

const HISTORY_PAGE_SIZE = 20;

function ReportHistory() {
    const db = useFirestore();
    const user = useCtcUser();
    const { resend, busyId } = useResend();
    const [openId, setOpenId] = useState<string | null>(null);
    const ref = useMemo(() => collection(db, CTC_REPORT_REQUESTS), [db]);
    // Desk sees every request; bank users only their own customer's (firestore.rules requires the filter)
    const { constraints, key } = useMemo(() => ({
        key: `reports:${user.isDesk ? "desk" : user.customerId ?? "-"}`,
        constraints: [...(user.isDesk ? [] : [where("customerId", "==", user.customerId ?? "-")]), orderBy("requestedAt", "desc")],
    }), [user.isDesk, user.customerId]);
    const page = useLivePagedQuery<CtcReportRequest>(ref, constraints, key, HISTORY_PAGE_SIZE);
    const open = page.rows.find((r) => r.id === openId);

    return (
        <CtcPanel icon={<IconHistory size={20} />} title="Report history" description={`${user.isDesk ? "All requests" : "Requests for your customer"} · live`}>
            <CtcQueryError error={page.error} />
            <Table.ScrollContainer minWidth={900}>
                <Table striped highlightOnHover withTableBorder>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Report</Table.Th><Table.Th>Request date</Table.Th><Table.Th>Filters</Table.Th><Table.Th>Requested by</Table.Th>
                            <Table.Th>Status</Table.Th><Table.Th ta="right">Orders</Table.Th><Table.Th>Last sent</Table.Th><Table.Th />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {page.rows.map((r) => {
                            const lastSend = r.sends[r.sends.length - 1];
                            return (
                                <Table.Tr key={r.id} style={{ cursor: "pointer" }} onClick={() => setOpenId(r.id!)}>
                                    <Table.Td><Text size="sm" fw={500}>{r.title}</Text></Table.Td>
                                    <Table.Td style={{ whiteSpace: "nowrap" }}>{fmtDay(r.from)} – {fmtDay(r.to)}</Table.Td>
                                    <Table.Td><Text size="xs" c="dimmed" lineClamp={2}>{r.filterText}</Text></Table.Td>
                                    <Table.Td>{r.requestedByName}<Text size="xs" c="dimmed">{kuwaitTime(r.requestedAt) ?? "…"}</Text></Table.Td>
                                    <Table.Td><StatusBadge r={r} /></Table.Td>
                                    <Table.Td ta="right">{r.rows !== undefined ? nf(r.rows) : "—"}{r.parts && r.parts > 1 ? <Text size="xs" c="dimmed">{r.parts} parts</Text> : null}</Table.Td>
                                    <Table.Td>
                                        {lastSend ? <>{kuwaitTime(lastSend.at)}<Text size="xs" c="dimmed">{r.sends.length > 1 ? `${r.sends.length} sends · ` : ""}{lastSend.byName}</Text></> : "—"}
                                    </Table.Td>
                                    <Table.Td onClick={(e) => e.stopPropagation()}>
                                        <Group gap={4} wrap="nowrap" justify="flex-end">
                                            <Tooltip label="Details and sends"><ActionIcon variant="subtle" color="gray" onClick={() => setOpenId(r.id!)} aria-label="Details"><IconEye size={16} /></ActionIcon></Tooltip>
                                            {!user.isViewOnly && (
                                                <Tooltip label={isBusy(r) ? "Being prepared" : "Build again and email"}>
                                                    <ActionIcon variant="subtle" onClick={() => resend(r)} loading={busyId === r.id} disabled={isBusy(r)} aria-label="Re-send"><IconMailForward size={16} /></ActionIcon>
                                                </Tooltip>
                                            )}
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            );
                        })}
                        {!page.loading && page.rows.length === 0 && (
                            <Table.Tr><Table.Td colSpan={8}><Text size="sm" c="dimmed" ta="center" py="md">No reports yet. Generate one above; it is emailed when ready.</Text></Table.Td></Table.Tr>
                        )}
                    </Table.Tbody>
                </Table>
            </Table.ScrollContainer>
            {page.loading && <Progress value={100} animated size="xs" mt="xs" />}
            <CtcPager page={page} />
            {open && <ReportRequestModal r={open} onClose={() => setOpenId(null)} />}
        </CtcPanel>
    );
}

export const CtcReports = () => {
    const user = useCtcUser();
    // Report emails / logo: ctc_backoffice and ctc_admin only, the people firestore.rules lets write ctc_config
    const canEditLogo = user.isCtcBackoffice;
    const { data: settings } = useLiveDoc<CtcReportSettings>(CTC_REPORT_SETTINGS_DOC);
    const [open, setOpen] = useState<CtcReportKind | null>(null);
    const [logoOpen, setLogoOpen] = useState(false);
    const [emailsOpen, setEmailsOpen] = useState(false);

    return (
        <Stack>
            <Group justify="space-between">
                <Title order={4}>Reports</Title>
                {canEditLogo && (
                    <Group gap="xs">
                        <Button variant="default" leftSection={<IconMail size={16} />} onClick={() => setEmailsOpen(true)}>
                            Report emails <Badge ml={6} size="xs" variant="light" color={settings?.emails?.length ? "teal" : "gray"}>{settings?.emails?.length ?? 0}</Badge>
                        </Button>
                        <Button variant="default" leftSection={<IconPhoto size={16} />} onClick={() => setLogoOpen(true)}>
                            Report logo {settings?.logo ? <Badge ml={6} size="xs" variant="light" color="teal">set</Badge> : null}
                        </Button>
                    </Group>
                )}
            </Group>
            <SimpleGrid cols={{ base: 1, md: 2 }}>
                {(Object.keys(CTC_REPORTS) as CtcReportKind[]).map((kind) => (
                    <Card key={kind} withBorder padding="lg" radius="md">
                        <Group align="flex-start" wrap="nowrap" mb="md">
                            <ThemeIcon size={42} radius="md" variant="light">{kind === "summary" ? <IconReportAnalytics size={24} /> : <IconFileSpreadsheet size={24} />}</ThemeIcon>
                            <div>
                                <Text fw={600}>{CTC_REPORTS[kind].title}</Text>
                                <Text size="sm" c="dimmed">{description(kind, ctcHidesBrokerCommission(user))}</Text>
                            </div>
                        </Group>
                        <Group justify="space-between" mt="auto">
                            <Text size="xs" c="dimmed">{visibleColumns(kind, ctcHidesBrokerCommission(user)).length} columns · Excel by email</Text>
                            {!user.isViewOnly && <Button leftSection={<IconMail size={16} />} onClick={() => setOpen(kind)}>Generate</Button>}
                        </Group>
                    </Card>
                ))}
            </SimpleGrid>
            <ReportHistory />
            {open && <GenerateReportModal kind={open} onClose={() => setOpen(null)} />}
            {emailsOpen && <ReportEmailsModal settings={settings ?? undefined} onClose={() => setEmailsOpen(false)} />}
            {logoOpen && <ReportLogoModal settings={settings ?? undefined} onClose={() => setLogoOpen(false)} />}
        </Stack>
    );
};

export default CtcReports;
